/**
 * PassportCountryPage - Página individual de un país en el pasaporte
 * Diseño PRO tipo diario de viajes / scrapbook con:
 * - Header con sello estilo pasaporte real
 * - Washi tape decorativo
 * - Fotos polaroid con cinta adhesiva
 * - Notas manuscritas sobre papel rayado
 * - Elementos decorativos (avión, sellos, stamps)
 */

import React, { useState, useRef } from "react";
import {
	Box,
	Typography,
	IconButton,
	TextField,
	Rating,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import FlightIcon from "@mui/icons-material/Flight";

/* Tokens Dashboard (modales Editar ciudad / Eliminar ciudad) */
const DASHBOARD = {
	primary: "#6366F1",
	primaryDark: "#4F46E5",
	textMuted: "#64748B",
	border: "rgba(0, 0, 0, 0.06)",
	radiusCard: "20px",
	radiusButton: "12px",
	shadowMd: "0 4px 16px rgba(0, 0, 0, 0.08)",
	shadowPrimary: "0 4px 16px rgba(99, 102, 241, 0.35)",
	bgCard: "#fff",
	bgSubtle: "rgba(0, 0, 0, 0.02)",
};

// Paletas completas por país (principal, claro, oscuro)
const getThemePalette = (countryCode) => {
	const code = (countryCode || "").toUpperCase();
	const palettes = {
		JP: {
			main: "#c44569",
			light: "#fce4ec",
			dark: "#880e4f",
			accent: "#f8bbd0",
		},
		KR: {
			main: "#3c6382",
			light: "#e3f2fd",
			dark: "#1a3a5c",
			accent: "#90caf9",
		},
		TH: {
			main: "#e58e26",
			light: "#fff8e1",
			dark: "#bf6d04",
			accent: "#ffe082",
		},
		CN: {
			main: "#b71540",
			light: "#fce4ec",
			dark: "#880e2e",
			accent: "#ef9a9a",
		},
		ID: {
			main: "#e55039",
			light: "#fbe9e7",
			dark: "#bf2d15",
			accent: "#ffab91",
		},
		MY: {
			main: "#0c2461",
			light: "#e8eaf6",
			dark: "#060f30",
			accent: "#9fa8da",
		},
		VN: {
			main: "#c0392b",
			light: "#ffebee",
			dark: "#962820",
			accent: "#ef9a9a",
		},
		ES: {
			main: "#e55039",
			light: "#fbe9e7",
			dark: "#bf2d15",
			accent: "#ffccbc",
		},
		FR: {
			main: "#1a237e",
			light: "#e8eaf6",
			dark: "#0d1042",
			accent: "#c5cae9",
		},
		IT: {
			main: "#2e7d32",
			light: "#e8f5e9",
			dark: "#1b5e20",
			accent: "#a5d6a7",
		},
		GB: {
			main: "#1565c0",
			light: "#e3f2fd",
			dark: "#0d3c7a",
			accent: "#90caf9",
		},
		DE: {
			main: "#37474f",
			light: "#eceff1",
			dark: "#1b2a33",
			accent: "#b0bec5",
		},
		PT: {
			main: "#2e7d32",
			light: "#e8f5e9",
			dark: "#1b5e20",
			accent: "#a5d6a7",
		},
		NL: {
			main: "#e65100",
			light: "#fff3e0",
			dark: "#bf4400",
			accent: "#ffcc80",
		},
		BE: {
			main: "#f57f17",
			light: "#fffde7",
			dark: "#c66900",
			accent: "#fff176",
		},
		US: {
			main: "#1565c0",
			light: "#e3f2fd",
			dark: "#0d3c7a",
			accent: "#90caf9",
		},
		MX: {
			main: "#2e7d32",
			light: "#e8f5e9",
			dark: "#1b5e20",
			accent: "#a5d6a7",
		},
		BR: {
			main: "#2e7d32",
			light: "#e8f5e9",
			dark: "#1b5e20",
			accent: "#fff176",
		},
		AR: {
			main: "#0277bd",
			light: "#e1f5fe",
			dark: "#01579b",
			accent: "#81d4fa",
		},
		AU: {
			main: "#ef6c00",
			light: "#fff3e0",
			dark: "#c55a00",
			accent: "#ffcc80",
		},
		NZ: {
			main: "#37474f",
			light: "#eceff1",
			dark: "#1b2a33",
			accent: "#b0bec5",
		},
		EG: {
			main: "#c9a84c",
			light: "#fffde7",
			dark: "#a08030",
			accent: "#fff176",
		},
		MA: {
			main: "#c62828",
			light: "#ffebee",
			dark: "#961f1f",
			accent: "#ef9a9a",
		},
		GR: {
			main: "#0d47a1",
			light: "#e3f2fd",
			dark: "#072e6b",
			accent: "#90caf9",
		},
		TR: {
			main: "#c62828",
			light: "#ffebee",
			dark: "#961f1f",
			accent: "#ef9a9a",
		},
		IN: {
			main: "#e65100",
			light: "#fff3e0",
			dark: "#b33f00",
			accent: "#ffcc80",
		},
		SG: {
			main: "#c62828",
			light: "#ffebee",
			dark: "#961f1f",
			accent: "#ef9a9a",
		},
	};
	return (
		palettes[code] || {
			main: "#5c6bc0",
			light: "#e8eaf6",
			dark: "#3949ab",
			accent: "#c5cae9",
		}
	);
};

/* ─── Polaroid con cinta adhesiva ─── */
const PolaroidPhoto = ({ photo, onDelete, rotation, tapeAngle, isFirst }) => {
	const [showDelete, setShowDelete] = useState(false);

	return (
		<Box
			onMouseEnter={() => setShowDelete(true)}
			onMouseLeave={() => setShowDelete(false)}
			sx={{
				width: isFirst ? { xs: "85%", sm: "80%" } : { xs: "42%", sm: "44%" },
				backgroundColor: "#fff",
				padding: isFirst ? "8px 8px 32px 8px" : "5px 5px 22px 5px",
				boxShadow: "0 4px 20px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.08)",
				transform: `rotate(${rotation}deg)`,
				transition: "all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
				position: "relative",
				flexShrink: 0,
				"&:hover": {
					transform: "rotate(0deg) scale(1.04)",
					boxShadow: "0 12px 35px rgba(0,0,0,0.18)",
					zIndex: 10,
				},
			}}
		>
			{/* Cinta adhesiva */}
			<Box
				sx={{
					position: "absolute",
					top: -8,
					left: "50%",
					transform: `translateX(-50%) rotate(${tapeAngle || 0}deg)`,
					width: isFirst ? 70 : 45,
					height: isFirst ? 18 : 13,
					background:
						"linear-gradient(180deg, rgba(255,235,180,0.85) 0%, rgba(240,220,160,0.75) 100%)",
					borderRadius: "1px",
					zIndex: 2,
					boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
					"&::before": {
						content: '""',
						position: "absolute",
						inset: 0,
						background: `repeating-linear-gradient(
							90deg,
							transparent,
							transparent 3px,
							rgba(200,180,120,0.15) 3px,
							rgba(200,180,120,0.15) 4px
						)`,
					},
				}}
			/>

			{/* Imagen */}
			<Box
				sx={{
					width: "100%",
					paddingTop: isFirst ? "75%" : "100%",
					position: "relative",
					backgroundColor: "#f5f0ea",
					overflow: "hidden",
					borderRadius: "1px",
				}}
			>
				<Box
					component="img"
					src={photo.url}
					alt={photo.caption || "Foto de viaje"}
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						objectFit: "cover",
					}}
				/>
			</Box>

			{/* Caption manuscrito */}
			{photo.caption && (
				<Typography
					sx={{
						color: "#555",
						textAlign: "center",
						mt: 0.75,
						fontFamily: "'Caveat', cursive",
						fontSize: isFirst
							? { xs: "0.85rem", sm: "0.95rem" }
							: { xs: "0.65rem", sm: "0.72rem" },
						lineHeight: 1.3,
						px: 0.5,
					}}
				>
					{photo.caption}
				</Typography>
			)}

			{/* Botón eliminar */}
			{showDelete && onDelete && (
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onDelete(photo.id);
					}}
					sx={{
						position: "absolute",
						top: -10,
						right: -10,
						backgroundColor: "rgba(220, 53, 69, 0.9)",
						color: "#fff",
						width: 24,
						height: 24,
						boxShadow: "0 2px 8px rgba(220,53,69,0.3)",
						"&:hover": { backgroundColor: "#dc3545" },
					}}
				>
					<CloseIcon sx={{ fontSize: 14 }} />
				</IconButton>
			)}
		</Box>
	);
};

/* ─── Componente principal ─── */
const PassportCountryPage = ({
	country,
	visits = [],
	pageNumber,
	onUpdate,
	onDelete,
	onDeleteVisit,
	onAddVisit,
	onAddPhoto,
	onDeletePhoto,
	readOnly = false,
}) => {
	const [editing, setEditing] = useState(false);
	const [editNotes, setEditNotes] = useState(country.notes || "");
	const [editRating, setEditRating] = useState(country.rating || 0);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const fileInputRef = useRef(null);

	// Editar una ciudad
	const [editingVisitId, setEditingVisitId] = useState(null);
	const [editCity, setEditCity] = useState("");
	const [editVisitDate, setEditVisitDate] = useState("");
	const [editVisitNotes, setEditVisitNotes] = useState("");

	// Confirmar eliminar ciudad
	const [confirmDeleteVisitId, setConfirmDeleteVisitId] = useState(null);

	// Añadir nueva ciudad (mismo país)
	const [addCityOpen, setAddCityOpen] = useState(false);
	const [newCity, setNewCity] = useState("");
	const [newVisitDate, setNewVisitDate] = useState("");
	const [newVisitNotes, setNewVisitNotes] = useState("");

	const palette = getThemePalette(country.countryCode);
	const displayName = country.countryName;
	const hasCities = visits.some((v) => v.city && v.city.trim());
	const citiesWithData = visits.filter((v) => v.city && v.city.trim());
	// En modo edición mostrar todas las visitas (incluidas sin ciudad) y botones editar/borrar/añadir
	const listToShow =
		!readOnly && editing && onUpdate && onDeleteVisit
			? visits
			: citiesWithData;
	const showCityActions = !readOnly && editing && onUpdate && onDeleteVisit;

	const openEditCity = (v) => {
		setEditingVisitId(v.id);
		setEditCity(v.city || "");
		setEditVisitDate(v.visitDate || "");
		setEditVisitNotes(v.notes || "");
	};

	const handleSaveEditCity = () => {
		if (editingVisitId && onUpdate) {
			onUpdate(editingVisitId, {
				city: editCity.trim() || null,
				visitDate: editVisitDate || null,
				notes: editVisitNotes.trim() || null,
			});
		}
		setEditingVisitId(null);
	};

	const handleConfirmDeleteVisit = () => {
		if (confirmDeleteVisitId && onDeleteVisit) {
			onDeleteVisit(confirmDeleteVisitId);
		}
		setConfirmDeleteVisitId(null);
	};

	const handleAddCity = async () => {
		if (!onAddVisit) return;
		await onAddVisit(country.countryCode, country.countryName, {
			city: newCity.trim() || null,
			visitDate: newVisitDate || null,
			notes: newVisitNotes.trim() || null,
		});
		setAddCityOpen(false);
		setNewCity("");
		setNewVisitDate("");
		setNewVisitNotes("");
	};

	const handleSaveNotes = () => {
		onUpdate(country.id, { notes: editNotes, rating: editRating });
		setEditing(false);
	};

	const handlePhotoUpload = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (ev) => {
			const img = new window.Image();
			img.onload = () => {
				const canvas = document.createElement("canvas");
				const MAX_SIZE = 800;
				let { width, height } = img;

				if (width > MAX_SIZE || height > MAX_SIZE) {
					if (width > height) {
						height = (height / width) * MAX_SIZE;
						width = MAX_SIZE;
					} else {
						width = (width / height) * MAX_SIZE;
						height = MAX_SIZE;
					}
				}

				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext("2d");
				ctx.drawImage(img, 0, 0, width, height);

				const base64 = canvas.toDataURL("image/jpeg", 0.8);
				onAddPhoto({ photo: base64, caption: "" });
			};
			img.src = ev.target.result;
		};
		reader.readAsDataURL(file);
		e.target.value = "";
	};

	const photos = country.photos || [];
	const photoRotations = photos.map((_, i) => {
		const vals = [-3, 2.5, -1.8, 3.2, -2.2, 1.5, -3.5, 2, -1, 3.8];
		return vals[i % vals.length];
	});
	const tapeAngles = photos.map((_, i) => {
		const vals = [3, -5, 7, -2, 4, -6, 2, -3, 5, -4];
		return vals[i % vals.length];
	});

	// Fecha formateada
	const visitDateFormatted = country.visitDate
		? new Date(country.visitDate + "T00:00:00").toLocaleDateString("es-ES", {
				year: "numeric",
				month: "long",
		  })
		: null;

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				background: "#f7f3ec",
				display: "flex",
				flexDirection: "column",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* ── Textura de papel kraft sutil ── */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.35,
					backgroundImage: `
						radial-gradient(ellipse at 15% 15%, rgba(210,195,170,0.3) 0%, transparent 60%),
						radial-gradient(ellipse at 85% 85%, rgba(210,195,170,0.2) 0%, transparent 60%)
					`,
					pointerEvents: "none",
				}}
			/>
			{/* Líneas de cuaderno sutiles */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.06,
					backgroundImage: `repeating-linear-gradient(
						0deg,
						transparent,
						transparent 28px,
						rgba(180,160,130,1) 28px,
						rgba(180,160,130,1) 29px
					)`,
					backgroundPosition: "0 120px",
					pointerEvents: "none",
				}}
			/>

			{/* ── HEADER PREMIUM estilo pasaporte ── */}
			<Box
				sx={{
					position: "relative",
					background: `linear-gradient(160deg, ${palette.dark} 0%, ${palette.main} 45%, ${palette.dark} 100%)`,
					pt: { xs: 2.5, sm: 3 },
					pb: { xs: 2.5, sm: 3 },
					px: { xs: 2.5, sm: 3 },
					overflow: "hidden",
				}}
			>
				{/* Capa 1: Textura grano de piel / papel sobre el gradiente */}
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						opacity: 0.06,
						backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
						backgroundSize: "150px 150px",
						pointerEvents: "none",
					}}
				/>

				{/* Capa 2: Patrón guilloche sutil (tipo pasaporte real) */}
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						opacity: 0.05,
						backgroundImage: `
							repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,1) 3px, rgba(255,255,255,1) 3.5px),
							repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,1) 3px, rgba(255,255,255,1) 3.5px)
						`,
						pointerEvents: "none",
					}}
				/>

				{/* Capa 3: Viñeta interior (bordes oscuros) */}
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						background: `radial-gradient(ellipse at 50% 50%, transparent 50%, ${palette.dark}55 100%)`,
						pointerEvents: "none",
					}}
				/>

				{/* Marco dorado interior sutil */}
				<Box
					sx={{
						position: "absolute",
						top: 6,
						left: 6,
						right: 6,
						bottom: 6,
						border: "0.5px solid rgba(255,215,0,0.12)",
						borderRadius: "2px",
						pointerEvents: "none",
						"&::before": {
							content: '""',
							position: "absolute",
							top: 3,
							left: 3,
							right: 3,
							bottom: 3,
							border: "0.5px solid rgba(255,215,0,0.06)",
							borderRadius: "1px",
						},
					}}
				/>

				{/* Código de país grande como marca de agua */}
				<Typography
					sx={{
						position: "absolute",
						top: "50%",
						right: { xs: 60, sm: 80 },
						transform: "translateY(-50%)",
						fontSize: { xs: "4.5rem", sm: "5.5rem" },
						fontWeight: 900,
						fontFamily: "'Playfair Display', serif",
						color: "rgba(255,255,255,0.04)",
						lineHeight: 1,
						letterSpacing: "0.05em",
						pointerEvents: "none",
						userSelect: "none",
					}}
				>
					{country.countryCode}
				</Typography>

				{/* Avión decorativo — trazo punteado de vuelo */}
				<Box
					sx={{
						position: "absolute",
						top: { xs: 10, sm: 12 },
						right: { xs: 10, sm: 14 },
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						pointerEvents: "none",
					}}
				>
					<Box
						sx={{
							width: { xs: 28, sm: 36 },
							height: "1px",
							backgroundImage:
								"linear-gradient(90deg, rgba(255,255,255,0.15) 50%, transparent 50%)",
							backgroundSize: "6px 1px",
						}}
					/>
					<FlightIcon
						sx={{
							fontSize: { xs: 14, sm: 16 },
							color: "rgba(255,255,255,0.18)",
							transform: "rotate(45deg)",
						}}
					/>
				</Box>

				{/* ── Contenido: Nombre + info ── */}
				<Box sx={{ position: "relative", zIndex: 1 }}>
					<Box sx={{ mb: 0.5 }}>
						{/* Etiqueta tipo pasaporte */}
						<Typography
							sx={{
								fontSize: "0.45rem",
								fontWeight: 700,
								color: "rgba(255,215,0,0.45)",
								textTransform: "uppercase",
								letterSpacing: "0.35em",
								mb: 0.5,
								fontFamily: "'Playfair Display', serif",
							}}
						>
							Destination
						</Typography>

						{/* Nombre del país */}
						<Typography
							sx={{
								fontSize: { xs: "1.4rem", sm: "1.6rem" },
								fontWeight: 700,
								color: "#fff",
								fontFamily: "'Playfair Display', serif",
								lineHeight: 1.1,
								textShadow:
									"0 1px 3px rgba(0,0,0,0.3), 0 0 10px rgba(0,0,0,0.1)",
								letterSpacing: "0.02em",
							}}
						>
							{displayName}
						</Typography>

						{/* Sub-info: fecha de la primera visita */}
						{visitDateFormatted && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.75,
									mt: 0.5,
								}}
							>
								<Typography
									sx={{
										fontSize: "0.6rem",
										color: "rgba(255,255,255,0.55)",
										fontStyle: "italic",
										letterSpacing: "0.03em",
									}}
								>
									{visitDateFormatted}
								</Typography>
							</Box>
						)}
					</Box>

					{/* Rating (estrellas doradas) */}
					{country.rating > 0 && !editing && (
						<Rating
							value={country.rating}
							readOnly
							size="small"
							sx={{
								mt: 0.75,
								"& .MuiRating-iconFilled": {
									color: "#FFD700",
									filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
								},
								"& .MuiRating-iconEmpty": {
									color: "rgba(255,255,255,0.2)",
								},
							}}
						/>
					)}
				</Box>

				{/* ── Sello "VISITED" estilo tinta real ── */}
				<Box
					sx={{
						position: "absolute",
						top: { xs: 10, sm: 8 },
						right: { xs: 34, sm: 44 },
						width: { xs: 62, sm: 74 },
						height: { xs: 62, sm: 74 },
						borderRadius: "50%",
						border: `2.5px solid rgba(255,255,255,0.45)`,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						transform: "rotate(3deg)",
						mixBlendMode: "overlay",
						opacity: 0.65,
						/* Simulación de irregularidad de tinta: doble borde */
						"&::before": {
							content: '""',
							position: "absolute",
							inset: 3,
							borderRadius: "50%",
							border: "1.5px dashed rgba(255,255,255,0.35)",
						},
						/* Borde exterior difuso — efecto tinta sangrada */
						"&::after": {
							content: '""',
							position: "absolute",
							inset: -2,
							borderRadius: "50%",
							border: "1px solid rgba(255,255,255,0.08)",
						},
						/* Efecto "opacidad irregular" via gradiente radial */
						backgroundImage: `radial-gradient(
							circle at 35% 40%,
							rgba(255,255,255,0.08) 0%,
							transparent 45%,
							rgba(255,255,255,0.04) 65%,
							transparent 100%
						)`,
					}}
				>
					<Typography
						sx={{
							fontSize: { xs: "0.38rem", sm: "0.42rem" },
							color: "rgba(255,255,255,0.85)",
							fontWeight: 800,
							textTransform: "uppercase",
							letterSpacing: "0.18em",
							lineHeight: 1,
							textShadow: "0 0 2px rgba(255,255,255,0.3)",
						}}
					>
						VISITED
					</Typography>
					<Typography
						sx={{
							fontSize: { xs: "0.75rem", sm: "0.85rem" },
							color: "rgba(255,255,255,0.9)",
							fontWeight: 800,
							lineHeight: 1,
							mt: 0.3,
							fontFamily: "'Playfair Display', serif",
							letterSpacing: "0.05em",
							textShadow: "0 0 3px rgba(255,255,255,0.25)",
						}}
					>
						{country.countryCode}
					</Typography>
					{visitDateFormatted && (
						<Typography
							sx={{
								fontSize: "0.32rem",
								color: "rgba(255,255,255,0.55)",
								mt: 0.25,
								lineHeight: 1,
								letterSpacing: "0.05em",
							}}
						>
							{new Date(country.visitDate + "T00:00:00").toLocaleDateString(
								"es-ES",
								{
									day: "2-digit",
									month: "2-digit",
									year: "2-digit",
								}
							)}
						</Typography>
					)}
				</Box>
			</Box>

			{/* ── Washi tape decorativo (transición header→contenido) ── */}
			<Box
				sx={{
					height: 10,
					background: `repeating-linear-gradient(
						-45deg,
						${palette.accent},
						${palette.accent} 4px,
						${palette.light} 4px,
						${palette.light} 8px
					)`,
					opacity: 0.7,
				}}
			/>

			{/* ── Contenido scrollable ── */}
			<Box
				sx={{
					flex: 1,
					overflow: "auto",
					px: { xs: 2, sm: 2.5 },
					py: 2,
					position: "relative",
					"&::-webkit-scrollbar": { width: 4 },
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: "rgba(0,0,0,0.08)",
						borderRadius: 2,
					},
				}}
			>
				{/* ── Ciudades visitadas (editable cuando !readOnly) ── */}
				{(hasCities || !readOnly) && (
					<Box
						sx={{
							mb: 2,
							position: "relative",
							backgroundColor: "rgba(255,255,255,0.5)",
							borderRadius: "8px",
							px: 2,
							py: 1.5,
							borderLeft: `3px solid ${palette.main}44`,
							boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
						}}
					>
						<Typography
							sx={{
								fontSize: "0.55rem",
								fontWeight: 700,
								color: palette.main,
								textTransform: "uppercase",
								letterSpacing: "0.15em",
								mb: 1,
								opacity: 0.7,
							}}
						>
							Ciudades visitadas
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
							{listToShow.map((v, i) => {
								const dateStr = v.visitDate
									? new Date(
											v.visitDate + "T00:00:00"
									  ).toLocaleDateString("es-ES", {
											year: "numeric",
											month: "short",
											day: "numeric",
									  })
									: null;
								const cityLabel = (v.city && v.city.trim()) ? v.city.trim() : "(Sin ciudad)";
								return (
									<Box
										key={v.id || i}
										sx={{
											display: "flex",
											flexWrap: "wrap",
											alignItems: "baseline",
											gap: 0.5,
											py: 0.5,
											pr: showCityActions ? 5 : 0,
											position: "relative",
											borderBottom:
												i < listToShow.length - 1
													? "1px solid rgba(0,0,0,0.06)"
													: "none",
										}}
									>
										<Typography
											sx={{
												fontSize: "0.9rem",
												fontWeight: 600,
												color: (v.city && v.city.trim()) ? "#2c2c2c" : "rgba(0,0,0,0.45)",
												fontFamily: "'Playfair Display', serif",
												fontStyle: !(v.city && v.city.trim()) ? "italic" : "normal",
											}}
										>
											{cityLabel}
										</Typography>
										{dateStr && (
											<Typography
												sx={{
													fontSize: "0.7rem",
													color: "rgba(0,0,0,0.45)",
													fontStyle: "italic",
												}}
											>
												{dateStr}
											</Typography>
										)}
										{v.notes && v.notes.trim() && (
											<Typography
												sx={{
													fontSize: "0.75rem",
													color: "rgba(0,0,0,0.6)",
													fontFamily: "'Caveat', cursive",
													width: "100%",
													mt: 0.25,
													pl: 0.5,
													borderLeft: `2px solid ${palette.main}30`,
												}}
											>
												{v.notes.trim()}
											</Typography>
										)}
										{!readOnly && showCityActions && (
											<Box
												sx={{
													position: "absolute",
													top: 0,
													right: 0,
													display: "flex",
													gap: 0.25,
												}}
											>
												<Tooltip title="Editar ciudad">
													<IconButton
														size="small"
														onClick={() => openEditCity(v)}
														sx={{
															width: 28,
															height: 28,
															color: palette.main,
															"&:hover": {
																backgroundColor: `${palette.main}18`,
															},
														}}
													>
														<EditIcon sx={{ fontSize: 14 }} />
													</IconButton>
												</Tooltip>
												<Tooltip title="Eliminar ciudad">
													<IconButton
														size="small"
														onClick={() => setConfirmDeleteVisitId(v.id)}
														sx={{
															width: 28,
															height: 28,
															color: "rgba(0,0,0,0.4)",
															"&:hover": {
																color: "#dc3545",
																backgroundColor: "rgba(220,53,69,0.08)",
															},
														}}
													>
														<DeleteOutlineIcon sx={{ fontSize: 14 }} />
													</IconButton>
												</Tooltip>
											</Box>
										)}
									</Box>
								);
							})}
						</Box>
						{!readOnly && editing && onAddVisit && (
							<Button
								size="small"
								startIcon={<FlightIcon sx={{ fontSize: 14 }} />}
								onClick={() => setAddCityOpen(true)}
								sx={{
									mt: 1,
									fontSize: "0.8rem",
									fontWeight: 600,
									color: palette.main,
									textTransform: "none",
									borderRadius: "8px",
									border: `1px dashed ${palette.main}55`,
									"&:hover": {
										backgroundColor: `${palette.main}12`,
										borderColor: palette.main,
									},
								}}
							>
								Añadir ciudad
							</Button>
						)}
					</Box>
				)}

				{/* Dialog editar ciudad — estilo Dashboard */}
				<Dialog
					open={Boolean(editingVisitId)}
					onClose={() => setEditingVisitId(null)}
					PaperProps={{
						sx: {
							borderRadius: DASHBOARD.radiusCard,
							overflow: "hidden",
							background: DASHBOARD.bgCard,
							boxShadow: DASHBOARD.shadowMd,
							border: `1px solid ${DASHBOARD.border}`,
						},
					}}
				>
					<Box
						sx={{
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							py: 2,
							px: 2.5,
							textAlign: "center",
							boxShadow: DASHBOARD.shadowPrimary,
						}}
					>
						<Typography
							sx={{
								fontSize: "1.1rem",
								fontWeight: 700,
								color: "#fff",
							}}
						>
							Editar ciudad
						</Typography>
					</Box>
					<DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5 }}>
						<TextField
							label="Ciudad"
							fullWidth
							value={editCity}
							onChange={(e) => setEditCity(e.target.value)}
							placeholder="Ej: Tokio, París"
							size="small"
							sx={{
								mt: 2,
								mb: 4,
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": { color: DASHBOARD.primary },
							}}
						/>
						<TextField
							label="Fecha de visita (opcional)"
							type="date"
							fullWidth
							value={editVisitDate}
							onChange={(e) => setEditVisitDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
							size="small"
							sx={{
								mb: 4,
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": { color: DASHBOARD.primary },
							}}
						/>
						<TextField
							label="Notas (opcional)"
							fullWidth
							multiline
							minRows={2}
							value={editVisitNotes}
							onChange={(e) => setEditVisitNotes(e.target.value)}
							placeholder="Recuerdos de esta ciudad..."
							size="small"
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": { color: DASHBOARD.primary },
							}}
						/>
					</DialogContent>
					<DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
						<Button
							onClick={() => setEditingVisitId(null)}
							size="small"
							sx={{
								textTransform: "none",
								color: DASHBOARD.textMuted,
								borderRadius: DASHBOARD.radiusButton,
								"&:hover": {
									bgcolor: DASHBOARD.bgSubtle,
									color: "#1a1a2e",
								},
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveEditCity}
							size="small"
							sx={{
								textTransform: "none",
								borderRadius: DASHBOARD.radiusButton,
								fontWeight: 600,
								color: "#fff",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								boxShadow: DASHBOARD.shadowPrimary,
								"&:hover": {
									background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
									color: "#fff",
								},
								"&:focus, &.Mui-focusVisible": {
									color: "#fff",
								},
							}}
						>
							Guardar
						</Button>
					</DialogActions>
				</Dialog>

				{/* Dialog añadir ciudad — mismo país */}
				<Dialog
					open={addCityOpen}
					onClose={() => {
						setAddCityOpen(false);
						setNewCity("");
						setNewVisitDate("");
						setNewVisitNotes("");
					}}
					maxWidth="sm"
					fullWidth
					PaperProps={{
						sx: {
							borderRadius: DASHBOARD.radiusCard,
							overflow: "hidden",
							background: DASHBOARD.bgCard,
							boxShadow: DASHBOARD.shadowMd,
							border: `1px solid ${DASHBOARD.border}`,
							maxHeight: "90vh",
							m: 2,
						},
					}}
				>
					<Box
						sx={{
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							py: 2,
							px: 2,
							textAlign: "center",
							boxShadow: DASHBOARD.shadowPrimary,
							flexShrink: 0,
						}}
					>
						<Typography
							sx={{
								fontSize: { xs: "1rem", sm: "1.1rem" },
								fontWeight: 700,
								color: "#fff",
								wordBreak: "break-word",
								lineHeight: 1.3,
							}}
						>
							Añadir ciudad en {displayName}
						</Typography>
					</Box>
					<DialogContent
						dividers
						sx={{
							pt: 2.5,
							pb: 1,
							px: 2.5,
							overflowY: "auto",
							display: "flex",
							flexDirection: "column",
							gap: 0,
						}}
					>
						<TextField
							label="Ciudad"
							fullWidth
							value={newCity}
							onChange={(e) => setNewCity(e.target.value)}
							placeholder="Ej: Tokio, París"
							size="small"
							sx={{
								mt: 2,
								mb: 4,
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": { color: DASHBOARD.primary },
							}}
						/>
						<TextField
							label="Fecha de visita (opcional)"
							type="date"
							fullWidth
							value={newVisitDate}
							onChange={(e) => setNewVisitDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
							size="small"
							sx={{
								mb: 4,
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": { color: DASHBOARD.primary },
							}}
						/>
						<TextField
							label="Notas (opcional)"
							fullWidth
							multiline
							minRows={2}
							maxRows={4}
							value={newVisitNotes}
							onChange={(e) => setNewVisitNotes(e.target.value)}
							placeholder="Recuerdos de esta ciudad..."
							size="small"
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: DASHBOARD.radiusButton,
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: DASHBOARD.primary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root.Mui-focused": { color: DASHBOARD.primary },
							}}
						/>
					</DialogContent>
					<DialogActions
						sx={{
							px: 2.5,
							py: 2,
							pt: 1.5,
							pb: 2,
							borderTop: "1px solid rgba(0,0,0,0.08)",
							backgroundColor: "rgba(0,0,0,0.02)",
							flexShrink: 0,
						}}
					>
						<Button
							onClick={() => {
								setAddCityOpen(false);
								setNewCity("");
								setNewVisitDate("");
								setNewVisitNotes("");
							}}
							size="small"
							sx={{
								textTransform: "none",
								color: DASHBOARD.textMuted,
								borderRadius: DASHBOARD.radiusButton,
								"&:hover": {
									bgcolor: DASHBOARD.bgSubtle,
									color: "#1a1a2e",
								},
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleAddCity}
							size="small"
							sx={{
								textTransform: "none",
								borderRadius: DASHBOARD.radiusButton,
								fontWeight: 600,
								color: "#fff",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								boxShadow: DASHBOARD.shadowPrimary,
								"&:hover": {
									background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
									color: "#fff",
								},
								"&:focus, &.Mui-focusVisible": {
									color: "#fff",
								},
							}}
						>
							Añadir
						</Button>
					</DialogActions>
				</Dialog>

				{/* Dialog confirmar eliminar ciudad — estilo Dashboard */}
				<Dialog
					open={Boolean(confirmDeleteVisitId)}
					onClose={() => setConfirmDeleteVisitId(null)}
					PaperProps={{
						sx: {
							borderRadius: DASHBOARD.radiusCard,
							overflow: "hidden",
							background: DASHBOARD.bgCard,
							boxShadow: DASHBOARD.shadowMd,
							border: `1px solid ${DASHBOARD.border}`,
						},
					}}
				>
					<DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a2e" }}>
						¿Eliminar esta ciudad?
					</DialogTitle>
					<DialogContent sx={{ pt: 0 }}>
						<Typography sx={{ fontSize: "0.9rem", color: DASHBOARD.textMuted }}>
							Se eliminará la ciudad del registro. Las fotos y notas del país no
							se borran.
						</Typography>
					</DialogContent>
					<DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
						<Button
							onClick={() => setConfirmDeleteVisitId(null)}
							size="small"
							sx={{
								textTransform: "none",
								color: DASHBOARD.textMuted,
								borderRadius: DASHBOARD.radiusButton,
								"&:hover": {
									bgcolor: DASHBOARD.bgSubtle,
									color: "#1a1a2e",
								},
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={handleConfirmDeleteVisit}
							size="small"
							sx={{
								textTransform: "none",
								borderRadius: DASHBOARD.radiusButton,
								fontWeight: 600,
							}}
						>
							Eliminar
						</Button>
					</DialogActions>
				</Dialog>

				{/* ── Notas del viaje ── */}
				<Box
					sx={{
						mb: 2,
						position: "relative",
						backgroundColor: "rgba(255,255,255,0.5)",
						borderRadius: "8px",
						px: 2,
						py: 1.5,
						borderLeft: `3px solid ${palette.main}44`,
						boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
					}}
				>
					{/* Icono de pin / chincheta */}
					<Box
						sx={{
							position: "absolute",
							top: -6,
							right: 12,
							width: 14,
							height: 14,
							borderRadius: "50%",
							background: `linear-gradient(135deg, ${palette.main}, ${palette.dark})`,
							boxShadow: `0 2px 6px ${palette.main}55`,
							"&::after": {
								content: '""',
								position: "absolute",
								top: 3,
								left: 3,
								width: 4,
								height: 4,
								borderRadius: "50%",
								backgroundColor: "rgba(255,255,255,0.5)",
							},
						}}
					/>

					<Typography
						sx={{
							fontSize: "0.55rem",
							fontWeight: 700,
							color: palette.main,
							textTransform: "uppercase",
							letterSpacing: "0.15em",
							mb: 0.75,
							opacity: 0.7,
						}}
					>
						✎ Notas del viaje
					</Typography>

					{editing ? (
						<Box>
							<TextField
								multiline
								minRows={3}
								maxRows={6}
								fullWidth
								value={editNotes}
								onChange={(e) => setEditNotes(e.target.value)}
								placeholder="¿Qué es lo que más te gustó? ¿Una comida increíble? ¿Un atardecer inolvidable?..."
								variant="standard"
								sx={{
									"& .MuiInput-root": {
										fontFamily: "'Caveat', cursive",
										fontSize: { xs: "0.95rem", sm: "1.05rem" },
										color: "#3a3a3a",
										lineHeight: 1.7,
										"&::before": { borderColor: "rgba(0,0,0,0.08)" },
										"&::after": { borderColor: palette.main },
									},
								}}
							/>
							<Box
								sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}
							>
								<Typography
									sx={{
										fontSize: "0.65rem",
										color: "rgba(0,0,0,0.4)",
										fontWeight: 500,
									}}
								>
									Tu valoración:
								</Typography>
								<Rating
									value={editRating}
									onChange={(_, v) => setEditRating(v)}
									size="small"
									sx={{
										"& .MuiRating-iconFilled": { color: palette.main },
									}}
								/>
							</Box>
						</Box>
					) : (
						<Box>
							{country.notes ? (
								<Typography
									onClick={readOnly ? undefined : () => {
										setEditNotes(country.notes || "");
										setEditRating(country.rating || 0);
										setEditing(true);
									}}
									sx={{
										fontFamily: "'Caveat', cursive",
										fontSize: { xs: "1rem", sm: "1.1rem" },
										color: "#3a3a3a",
										lineHeight: 1.7,
										whiteSpace: "pre-wrap",
										cursor: readOnly ? "default" : "pointer",
										"&:hover": readOnly ? {} : { color: "#222" },
									}}
								>
									{country.notes}
								</Typography>
							) : !readOnly ? (
								<Typography
									onClick={() => {
										setEditNotes("");
										setEditRating(country.rating || 0);
										setEditing(true);
									}}
									sx={{
										fontFamily: "'Caveat', cursive",
										fontSize: { xs: "0.95rem", sm: "1.05rem" },
										color: "rgba(0,0,0,0.22)",
										cursor: "pointer",
										py: 1,
										textAlign: "center",
										transition: "all 0.2s ease",
										"&:hover": {
											color: palette.main,
											backgroundColor: `${palette.main}08`,
											borderRadius: "6px",
										},
									}}
								>
									Toca aquí para escribir tus recuerdos...
								</Typography>
							) : null}
						</Box>
					)}
				</Box>

				{/* ── Sección de fotos ── */}
				<Box sx={{ mb: 1.5 }}>
					<Typography
						sx={{
							fontSize: "0.55rem",
							fontWeight: 700,
							color: palette.main,
							textTransform: "uppercase",
							letterSpacing: "0.15em",
							mb: 1.5,
							opacity: 0.7,
							px: 0.5,
						}}
					>
						Mis fotos
					</Typography>

					{photos.length > 0 ? (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: { xs: 2, sm: 2.5 },
								justifyContent: "center",
								mb: 2,
							}}
						>
							{photos.map((photo, i) => (
								<PolaroidPhoto
									key={photo.id}
									photo={photo}
									rotation={photoRotations[i] || 0}
									tapeAngle={tapeAngles[i] || 0}
									isFirst={photos.length === 1 && i === 0}
									onDelete={readOnly ? null : onDeletePhoto}
								/>
							))}
						</Box>
					) : null}

					{/* Botón añadir foto - diseño mejorado */}
					{!readOnly && (<><Box
						onClick={() => fileInputRef.current?.click()}
						tabIndex={0}
						role="button"
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								fileInputRef.current?.click();
							}
						}}
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: 0.75,
							py: photos.length > 0 ? 2 : 3,
							mx: photos.length > 0 ? 0 : 2,
							border: `2px dashed ${palette.main}25`,
							borderRadius: "12px",
							cursor: "pointer",
							transition: "all 0.25s ease",
							backgroundColor: "rgba(255,255,255,0.3)",
							"&:hover": {
								borderColor: `${palette.main}55`,
								backgroundColor: `${palette.main}08`,
								transform: "translateY(-1px)",
								"& .add-icon": {
									backgroundColor: palette.main,
									color: "#fff",
								},
							},
							"&:focus": {
								outline: "none",
							},
							"&:focus-visible": {
								borderColor: `${palette.main}55`,
								backgroundColor: `${palette.main}08`,
								"& .add-icon": {
									backgroundColor: palette.main,
									color: "#fff",
								},
							},
						}}
					>
						<Box
							className="add-icon"
							sx={{
								width: 36,
								height: 36,
								borderRadius: "10px",
								backgroundColor: `${palette.main}15`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								transition: "all 0.25s ease",
							}}
						>
							<AddAPhotoIcon sx={{ fontSize: 18, color: palette.main }} />
						</Box>
						<Typography
							sx={{
								fontSize: "0.7rem",
								color: "rgba(0,0,0,0.35)",
								fontWeight: 500,
							}}
						>
							Añadir foto
						</Typography>
						{photos.length === 0 && (
							<Typography
								sx={{
									fontSize: "0.55rem",
									color: "rgba(0,0,0,0.2)",
									maxWidth: 200,
									textAlign: "center",
									lineHeight: 1.4,
								}}
							>
								Sube fotos de tu viaje y crea tu álbum polaroid
							</Typography>
						)}
					</Box>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						style={{ display: "none" }}
						onChange={handlePhotoUpload}
					/></>)}
				</Box>
			</Box>

			{/* ── Barra de acciones inferior ── */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					px: 2.5,
					py: 1,
					borderTop: "1px solid rgba(0,0,0,0.04)",
					backgroundColor: "rgba(255,255,255,0.4)",
				}}
			>
				<Typography
					sx={{
						fontSize: "0.5rem",
						color: "rgba(0,0,0,0.15)",
						letterSpacing: "0.08em",
						textTransform: "uppercase",
						fontFamily: "'Playfair Display', serif",
					}}
				>
					Pág. {pageNumber}
				</Typography>
				{!readOnly && (
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{editing ? (
						<>
							<Button
								size="small"
								variant="outlined"
								onClick={() => {
									setEditing(false);
									setEditNotes(country.notes || "");
									setEditRating(country.rating || 0);
								}}
								sx={{
									fontSize: "0.75rem",
									fontWeight: 600,
									color: "rgba(0,0,0,0.75)",
									textTransform: "none",
									borderRadius: "8px",
									borderColor: "rgba(0,0,0,0.23)",
									minHeight: 36,
									px: 1.5,
									"&:hover": {
										borderColor: "rgba(0,0,0,0.4)",
										backgroundColor: "rgba(0,0,0,0.04)",
									},
								}}
							>
								Cancelar
							</Button>
							<Button
								size="small"
								startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
								onClick={handleSaveNotes}
								sx={{
									fontSize: "0.75rem",
									fontWeight: 600,
									color: "#fff !important",
									textTransform: "none",
									backgroundColor: `${palette.main} !important`,
									borderRadius: "8px",
									px: 1.5,
									minHeight: 36,
									"& .MuiButton-startIcon *": { color: "#fff !important" },
									"&:hover": {
										backgroundColor: `${palette.dark} !important`,
										color: "#fff !important",
										"& .MuiButton-startIcon *": { color: "#fff !important" },
									},
								}}
							>
								Guardar
							</Button>
						</>
					) : (
						<>
							<Tooltip title="Editar página">
								<IconButton
									size="small"
									onClick={() => {
										setEditNotes(country.notes || "");
										setEditRating(country.rating || 0);
										setEditing(true);
									}}
									sx={{
										color: palette.main,
										width: 36,
										height: 36,
										backgroundColor: `${palette.main}18`,
										border: `1px solid ${palette.main}40`,
										"&:hover": {
											color: palette.dark,
											backgroundColor: `${palette.main}28`,
											borderColor: `${palette.main}60`,
										},
									}}
								>
									<EditIcon sx={{ fontSize: 18 }} />
								</IconButton>
							</Tooltip>
							<Tooltip title="Eliminar país">
								<IconButton
									size="small"
									onClick={() => setConfirmDelete(true)}
									sx={{
										color: "#b91c1c",
										width: 36,
										height: 36,
										backgroundColor: "rgba(185,28,28,0.1)",
										border: "1px solid rgba(185,28,28,0.3)",
										"&:hover": {
											color: "#dc2626",
											backgroundColor: "rgba(220,38,38,0.15)",
											borderColor: "rgba(185,28,28,0.5)",
										},
									}}
								>
									<DeleteOutlineIcon sx={{ fontSize: 18 }} />
								</IconButton>
							</Tooltip>
						</>
					)}
				</Box>
			)}
			</Box>

			{/* ── Dialog confirmar eliminación ── */}
			<Dialog
				open={confirmDelete}
				onClose={() => setConfirmDelete(false)}
				PaperProps={{
					sx: {
						borderRadius: "20px",
						p: 1,
						overflow: "hidden",
					},
				}}
			>
				<Box
					sx={{
						height: 4,
						background: `linear-gradient(90deg, ${palette.main}, ${palette.dark})`,
					}}
				/>
				<DialogTitle
					sx={{
						fontFamily: "'Playfair Display', serif",
						fontSize: "1.1rem",
						pt: 2.5,
					}}
				>
					¿Eliminar {displayName}?
				</DialogTitle>
				<DialogContent>
					<Typography
						sx={{
							fontSize: "0.85rem",
							color: "rgba(0,0,0,0.55)",
							lineHeight: 1.6,
						}}
					>
						Se eliminarán todas las fotos y notas de este destino. Esta acción
						no se puede deshacer.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					<Button
						onClick={() => setConfirmDelete(false)}
						sx={{
							textTransform: "none",
							color: "rgba(0,0,0,0.45)",
							borderRadius: "10px",
							px: 2.5,
						}}
					>
						Cancelar
					</Button>
					<Button
						onClick={() => {
							setConfirmDelete(false);
							onDelete();
						}}
						variant="contained"
						sx={{
							textTransform: "none",
							backgroundColor: "#dc3545",
							fontWeight: 600,
							borderRadius: "10px",
							px: 2.5,
							boxShadow: "0 4px 12px rgba(220,53,69,0.3)",
							"&:hover": { backgroundColor: "#c82333" },
						}}
					>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PassportCountryPage;
